import { z } from "zod";
import { zDiaCivil, zId, zInstanteUtc } from "./dominio.js";
/**
 * Trilha — a unidade de escopo do Noesis (SPEC §3, RN-01).
 *
 * Tudo do usuário é particionado por ela: itens, respostas, metas, caderno e
 * estatísticas. Por isso ela é a primeira coisa da F1: sem trilha não existe
 * onde pendurar nada.
 *
 * Nenhum limite de negócio é literal aqui (invariante 4). O schema valida
 * **forma** — que a meta é um inteiro positivo, que a data é uma data. Os
 * limites em vigor (mínimo e máximo da meta) vêm da configuração do servidor e
 * chegam ao navegador em `SessaoAtual.limites`, que é de onde a tela tira o
 * `min` e o `max` do campo.
 */
/**
 * O padrão de dias de descanso (RN-17): sábado e domingo.
 *
 * Mora aqui, e não numa constante do servidor nem da tela, porque é o mesmo
 * valor que a coluna traz por `DEFAULT` e que o formulário mostra marcado —
 * três lugares, uma definição (invariante 4).
 */
export const DESCANSO_PADRAO = [0, 6];
export const zTrilha = z.object({
    id: zId,
    nome: z.string().min(1),
    /** Marca curta para caber em lista e cabeçalho sem quebrar linha. */
    iniciais: z.string().min(1),
    descricao: z.string().nullable(),
    metaDiaria: z.number().int().positive(),
    /** Data-alvo da contagem regressiva (RN-20). Opcional: nem toda frente tem prova. */
    dataAlvo: zDiaCivil.nullable(),
    feriasAtivo: z.boolean(),
    arquivada: z.boolean(),
    ordem: z.number().int(),
    /**
     * Os dias da semana em que não se cobra a meta (RN-17): 0 domingo … 6 sábado.
     * Moram na trilha porque nem toda frente de estudo tem a mesma cadência.
     */
    diasDeDescanso: z.array(z.number().int().min(0).max(6)),
    /**
     * Os pesos da média bayesiana que ordena a fila de erradas (RN-45). Moram na
     * trilha porque são regra de negócio, não constante de código (invariante 4).
     */
    reincidenciaAlfa: z.number().positive(),
    reincidenciaBeta: z.number().positive(),
    /**
     * Teto de revisões por dia (Fase 5). A fila do dia mostra no máximo este
     * número, as mais atrasadas primeiro; o excedente continua vencido e
     * reaparece nos dias seguintes.
     */
    maximoRevisoesDia: z.number().int().positive(),
    criadoEm: zInstanteUtc,
    atualizadoEm: zInstanteUtc,
});
/** Texto vindo de formulário: sem espaço nas pontas, e vazio vira ausente. */
const zTextoOpcional = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .default(null);
/**
 * O teto de revisões com que uma trilha nasce (Fase 5).
 *
 * **Um número, um lugar.** Ele é o `default` do schema, o `DEFAULT` da coluna e
 * o que `criarTrilha` grava. Ele já divergiu uma vez nesta mesma fase: a coluna
 * nasceu com padrão, o schema também, e `criarTrilha` devolvia a linha que ela
 * mesma montou — sem o campo. O validador de resposta do contrato pegou
 * (invariante 2), e a saída não é repetir o 30 num quarto lugar.
 */
export const MAXIMO_REVISOES_DIA_PADRAO = 30;
export const zTrilhaEntrada = z.object({
    nome: z.string().trim().min(1, "Dê um nome à trilha").max(80, "No máximo 80 caracteres"),
    /**
     * Caixa alta no schema, não na tela: normalizar de um lado só deixa passar
     * quem chama a API direto, e aí a mesma trilha aparece de dois jeitos.
     */
    iniciais: z
        .string()
        .trim()
        .min(1, "Informe ao menos uma letra")
        .max(4, "No máximo 4 caracteres")
        .transform((v) => v.toUpperCase()),
    descricao: zTextoOpcional(500),
    metaDiaria: z.number({ message: "Informe um número" }).int("A meta é um número inteiro").positive("A meta precisa ser ao menos 1"),
    dataAlvo: zDiaCivil.nullable().default(null),
    /*
     * Dias de descanso (RN-17), pelo mesmo caminho de alfa e beta: o padrão
     * sábado e domingo mora aqui e na coluna, não numa constante do servidor.
     *
     * A normalização é do schema, não da tela: repetido some e a ordem é sempre
     * crescente, para "domingo e sábado" e "sábado e domingo" gravarem a mesma
     * linha. Quem chama a API direto passa pela mesma porta.
     */
    diasDeDescanso: z
        .array(z.number({ message: "Informe um dia da semana" }).int().min(0, "Dia da semana inválido").max(6, "Dia da semana inválido"))
        .max(7, "São sete dias na semana")
        .default(() => [...DESCANSO_PADRAO])
        .transform((v) => [...new Set(v)].sort((a, b) => a - b)),
    /*
     * Padrão da RN-45 aplicado aqui, e não numa constante do servidor: trilha
     * criada antes desta regra já tem o padrão na coluna, e trilha nova o recebe
     * pelo mesmo caminho. Os limites são de forma, não de negócio — a regra é
     * "positivo", e o valor é escolha do usuário.
     */
    reincidenciaAlfa: z.number({ message: "Informe um número" }).positive("Alfa precisa ser maior que zero").default(1),
    reincidenciaBeta: z.number({ message: "Informe um número" }).positive("Beta precisa ser maior que zero").default(3),
    /*
     * O padrão mora no schema pelo mesmo caminho de alfa e beta: trilha criada
     * antes desta regra já tem o padrão na coluna, e trilha nova o recebe daqui.
     * O `max` é de forma, não de negócio — quem quiser 200 revisões por dia pode.
     */
    maximoRevisoesDia: z
        .number({ message: "Informe um número" })
        .int("O teto é um número inteiro")
        .positive("O teto precisa ser ao menos 1")
        .max(1000, "No máximo mil por dia")
        .default(MAXIMO_REVISOES_DIA_PADRAO),
});
export const zTrilhaId = z.object({ id: zId });
export const zTrilhaArquivamento = z.object({ arquivada: z.boolean() });
/**
 * Exclusão de trilha exige o nome digitado (RN-25, invariante 11). A conferência
 * é do servidor: a tela pode ajudar, mas não é ela que decide.
 */
export const zTrilhaExclusao = z.object({ confirmacaoNome: z.string() });
/**
 * Toda resposta que mexe na seleção devolve a lista inteira e quem ficou ativa.
 * Assim a tela nunca precisa deduzir o novo estado a partir do que enviou — e
 * "excluí a ativa, quem entrou no lugar?" tem uma resposta só, a do servidor.
 */
export const zTrilhaSelecao = z.object({
    trilhas: z.array(zTrilha),
    ativaId: zId.nullable(),
});
export const zTrilhaLista = zTrilhaSelecao;
export const zTrilhaUma = zTrilhaSelecao.extend({ trilha: zTrilha });
/**
 * Quem assume quando a trilha ativa sai de cena — excluída ou arquivada.
 *
 * Pura de propósito: é a regra que decide se o usuário cai na tela guiada de
 * "nenhuma trilha selecionada" ou continua trabalhando, e essa decisão merece
 * teste sem banco no caminho.
 *
 * A escolha é a primeira não arquivada por `ordem`, com o `id` desempatando
 * para o resultado não depender da ordem de leitura do banco. Não sobrando
 * nenhuma, devolve `null` — e aí a RN-04 assume: tela guiada, nunca erro.
 */
export function escolherSucessora(trilhas, excluidaId = null) {
    const candidatas = trilhas
        .filter((t) => t.id !== excluidaId && !t.arquivada)
        .sort((a, b) => a.ordem - b.ordem || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    return candidatas[0]?.id ?? null;
}
//# sourceMappingURL=trilha.js.map